<script setup lang="ts">
import { ArrowLeftIcon, PaperclipIcon, XIcon } from "lucide-vue-next";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  formatByteSize,
  participantDisplayName,
} from "@/features/discussion-threads/formatters";
import type { DiscussionThreadAttachment } from "@/features/discussion-threads/types";
import type { MentionableGroup, MentionableUser } from "@/types";
import { formatDateTimeAR } from "@/utils/date";

interface Props {
  participantsUsers: MentionableUser[];
  participantsGroups: MentionableGroup[];
  attachments: DiscussionThreadAttachment[];
  createdAt: string;
  updatedAt: string;
  creatorName: string;
  creatorEmail: string;
  archived: boolean;
  archiveSwitchDisabled: boolean;
  participantRemovalInProgress: number | null;
  participantGroupRemovalInProgress: number | null;
  closeVariant?: "none" | "back" | "close";
  closeLabel?: string;
}

withDefaults(defineProps<Props>(), {
  closeVariant: "none",
  closeLabel: "Cerrar",
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "remove-user", participant: MentionableUser): void;
  (e: "remove-group", participant: MentionableGroup): void;
  (e: "archive-change", value: boolean): void;
}>();

const canRemoveParticipant = (participantCanRemove?: boolean) =>
  Boolean(participantCanRemove);
</script>

<template>
  <div class="flex h-full flex-col overflow-y-auto themed-scrollbar">
    <div class="flex items-center gap-3 px-5 py-4">
      <button
        v-if="closeVariant !== 'none'"
        :aria-label="closeLabel"
        class="-ml-1 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-foreground/[0.03] hover:text-foreground"
        type="button"
        @click="emit('close')"
      >
        <ArrowLeftIcon v-if="closeVariant === 'back'" class="h-5 w-5" />
        <XIcon v-else class="h-5 w-5" />
      </button>
      <h2 class="text-[13px] font-semibold tracking-tight text-foreground">
        Detalles
      </h2>
    </div>

    <div class="border-t border-border" />

    <div class="space-y-5 px-5 py-5">
      <section>
        <h3
          class="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70"
        >
          Participantes
        </h3>
        <div class="flex flex-wrap gap-1.5">
          <div
            v-for="participant in participantsGroups"
            :key="`group-${participant.id}`"
            class="inline-flex w-fit items-center rounded-md border text-[11px]"
          >
            <span class="truncate px-2 py-1">
              @{{ participant.name }}
            </span>
            <button
              v-if="canRemoveParticipant(participant.can_remove_participant)"
              type="button"
              class="flex items-center justify-center border-l px-1.5 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
              :disabled="Boolean(participantGroupRemovalInProgress)"
              @click="emit('remove-group', participant)"
            >
              <XIcon class="size-3" />
            </button>
          </div>

          <div
            v-for="participant in participantsUsers"
            :key="`user-${participant.id}`"
            class="inline-flex w-fit items-center rounded-md border text-[11px]"
          >
            <span class="truncate px-2 py-1">
              {{ participantDisplayName(participant) }}
            </span>
            <button
              v-if="canRemoveParticipant(participant.can_remove_participant)"
              type="button"
              class="flex items-center justify-center border-l px-1.5 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
              :disabled="Boolean(participantRemovalInProgress)"
              @click="emit('remove-user', participant)"
            >
              <XIcon class="size-3" />
            </button>
          </div>

          <p
            v-if="participantsGroups.length === 0 && participantsUsers.length === 0"
            class="text-[13px] text-muted-foreground"
          >
            Sin participantes.
          </p>
        </div>
      </section>

      <div class="border-t border-border" />

      <section>
        <h3
          class="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70"
        >
          Documentos adjuntos
        </h3>
        <ScrollArea v-if="attachments.length > 0" class="max-h-60">
          <div class="space-y-2 pr-3">
            <a
              v-for="(attachment, idx) in attachments"
              :key="`attachment-${idx}-${attachment.url}`"
              :href="attachment.url"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 text-left transition hover:bg-muted/40"
            >
              <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-muted">
                <PaperclipIcon class="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="truncate text-[12px] font-medium text-foreground">
                  {{ attachment.filename }}
                </p>
                <p class="text-[10px] text-muted-foreground/60">
                  {{ formatByteSize(attachment.byte_size) }}
                  <template v-if="attachment.author"> · {{ attachment.author }}</template>
                </p>
              </div>
            </a>
          </div>
        </ScrollArea>
        <p v-else class="text-[13px] text-muted-foreground">
          Sin archivos adjuntos.
        </p>
      </section>

      <div class="border-t border-border" />

      <section>
        <h3
          class="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70"
        >
          Metadatos
        </h3>
        <div class="space-y-2.5">
          <div class="flex items-center justify-between">
            <span class="text-[13px] text-muted-foreground">Creado</span>
            <span class="text-[13px] tabular-nums text-foreground">
              {{ createdAt ? formatDateTimeAR(createdAt) : "-" }}
            </span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[13px] text-muted-foreground">Actualizado</span>
            <span class="text-[13px] tabular-nums text-foreground">
              {{ updatedAt ? formatDateTimeAR(updatedAt) : "-" }}
            </span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[13px] text-muted-foreground">Creador</span>
            <span class="text-[13px] text-foreground">{{ creatorName }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[13px] text-muted-foreground">Email</span>
            <span class="text-[13px] text-foreground">{{ creatorEmail }}</span>
          </div>
          <div>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-muted-foreground">Archivado</span>
              <Switch
                :model-value="archived"
                :disabled="archiveSwitchDisabled"
                @update:model-value="emit('archive-change', $event)"
              />
            </div>
            <p class="mt-1.5 text-[11px] leading-snug text-muted-foreground/50">
              Solo el creador puede archivar. Cualquier participante puede desarchivar.
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
